/**
 * PDF Generator Utility
 * Generates professional PDF invoices
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatDate } from './formatters';
import { hexToRgb, DEFAULT_BRANDING } from '../services/brandingService';

/**
 * Generate PDF for an invoice
 * @param {Object} invoice - Invoice data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 * @returns {jsPDF} PDF document
 */
export const generateInvoicePDF = (invoice, userProfile = {}, branding = null) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;

  // Use branding if provided, otherwise defaults
  const brand = branding || DEFAULT_BRANDING;

  // Colors - use branding colors if available
  const primaryColor = hexToRgb(brand.primaryColor || '#2962ff');
  const secondaryColor = hexToRgb(brand.secondaryColor || '#2d3748');
  const accentColor = hexToRgb(brand.accentColor || '#e53e3e');
  const textColor = [33, 33, 33];
  const secondaryText = [117, 117, 117];
  
  let yPosition = 20;

  // Header Section - use branding company name or user profile
  const companyName = brand.companyName || userProfile.businessName || 'Your Business';
  const tagline = brand.tagline || '';

  pdf.setFontSize(24);
  pdf.setTextColor(...primaryColor);
  pdf.text(companyName, 20, yPosition);

  yPosition += 8;
  pdf.setFontSize(10);
  pdf.setTextColor(...secondaryText);

  // Add tagline if available
  if (tagline) {
    pdf.text(tagline, 20, yPosition);
    yPosition += 5;
  }

  if (userProfile.businessAddress) {
    const addressLines = userProfile.businessAddress.split('\n');
    addressLines.forEach(line => {
      pdf.text(line, 20, yPosition);
      yPosition += 5;
    });
  }

  // Invoice Title
  yPosition = 20;
  pdf.setFontSize(28);
  pdf.setTextColor(...primaryColor);
  pdf.text('INVOICE', pageWidth - 20, yPosition, { align: 'right' });

  yPosition += 10;
  pdf.setFontSize(10);
  pdf.setTextColor(...textColor);
  pdf.text(`Invoice #: ${invoice.invoiceNumber || 'N/A'}`, pageWidth - 20, yPosition, { align: 'right' });

  yPosition += 5;
  pdf.text(`Date: ${formatDate(invoice.issueDate) || 'N/A'}`, pageWidth - 20, yPosition, { align: 'right' });

  yPosition += 5;
  pdf.text(`Due Date: ${formatDate(invoice.dueDate) || 'N/A'}`, pageWidth - 20, yPosition, { align: 'right' });

  // Horizontal line
  yPosition = Math.max(yPosition, 60);
  pdf.setDrawColor(...primaryColor);
  pdf.setLineWidth(0.5);
  pdf.line(20, yPosition, pageWidth - 20, yPosition);
  
  yPosition += 15;

  // Bill To Section
  pdf.setFontSize(10);
  pdf.setTextColor(...secondaryText);
  pdf.text('BILL TO:', 20, yPosition);

  yPosition += 6;
  pdf.setFontSize(12);
  pdf.setTextColor(...textColor);
  pdf.text(invoice.clientName || 'Client', 20, yPosition);

  yPosition += 6;
  pdf.setFontSize(10);
  if (invoice.clientEmail) {
    pdf.text(invoice.clientEmail, 20, yPosition);
    yPosition += 5;
  }

  yPosition += 10;

  // Status Badge
  const statusX = pageWidth - 60;
  const statusY = yPosition - 15;

  // Status background
  const status = invoice.status || 'pending';
  if (status === 'paid') {
    pdf.setFillColor(209, 250, 229); // green
    pdf.setTextColor(6, 95, 70);
  } else if (status === 'pending' || status === 'sent') {
    pdf.setFillColor(254, 243, 199); // yellow
    pdf.setTextColor(146, 64, 14);
  } else {
    pdf.setFillColor(229, 231, 235); // gray
    pdf.setTextColor(55, 65, 81);
  }

  pdf.roundedRect(statusX, statusY, 40, 8, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.text(status.toUpperCase(), statusX + 20, statusY + 5.5, { align: 'center' });

  // Line Items Table
  pdf.setTextColor(...textColor);

  const lineItems = invoice.lineItems || [];
  const tableData = lineItems.map(item => {
    if (invoice.type === 'time-based' && item.hours !== undefined) {
      // Time-based invoice
      return [
        item.description || formatDate(item.date) || 'Item',
        `${(item.hours || 0).toFixed(2)} hrs`,
        formatCurrency(item.rate || 0),
        formatCurrency(item.amount || 0)
      ];
    } else {
      // Flat fee / manual invoice
      return [
        item.description || 'Item',
        item.quantity ? item.quantity.toString() : '1',
        item.rate ? formatCurrency(item.rate) : '-',
        formatCurrency(item.amount || 0)
      ];
    }
  });

  const tableHeaders = invoice.type === 'time-based' 
    ? [['Description', 'Hours', 'Rate', 'Amount']]
    : [['Description', 'Qty', 'Rate', 'Amount']];

  autoTable(pdf, {
    startY: yPosition,
    head: tableHeaders,
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: textColor,
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });

  // Get Y position after table
  yPosition = (pdf.lastAutoTable?.finalY || yPosition) + 10;

  // Totals Section
  const totalsX = pageWidth - 85;
  const totalsWidth = 65;

  // Subtotal
  pdf.setFontSize(10);
  pdf.setTextColor(...textColor);
  pdf.text('Subtotal:', totalsX, yPosition);
  pdf.text(formatCurrency(invoice.subtotal || invoice.totalAmount, invoice.currency), totalsX + totalsWidth, yPosition, { align: 'right' });

  yPosition += 6;

  // Discount (if applicable)
  if (invoice.discountAmount && invoice.discountAmount > 0) {
    pdf.setTextColor(76, 175, 80); // Green color for discount
    pdf.text(`Discount (${invoice.discountPercent || 0}%):`, totalsX, yPosition);
    pdf.text(`-${formatCurrency(invoice.discountAmount, invoice.currency)}`, totalsX + totalsWidth, yPosition, { align: 'right' });
    pdf.setTextColor(...textColor);
    yPosition += 6;
  }

  // Tax (if applicable)
  if (invoice.tax && invoice.tax > 0) {
    pdf.text('Tax:', totalsX, yPosition);
    pdf.text(formatCurrency(invoice.tax, invoice.currency), totalsX + totalsWidth, yPosition, { align: 'right' });
    yPosition += 6;
  }

  // Total (bold and larger)
  pdf.setDrawColor(...primaryColor);
  pdf.line(totalsX, yPosition - 2, pageWidth - 20, yPosition - 2);
  yPosition += 6;

  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(...primaryColor);
  pdf.text('TOTAL:', totalsX, yPosition);
  pdf.text(formatCurrency(invoice.totalAmount, invoice.currency), totalsX + totalsWidth, yPosition, { align: 'right' });
  
  yPosition += 10;

  // Notes Section
  if (invoice.notes) {
    yPosition += 5;
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...textColor);
    pdf.text('Notes:', 20, yPosition);
    
    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(...secondaryText);
    pdf.setFontSize(9);
    
    const notesLines = pdf.splitTextToSize(invoice.notes, pageWidth - 40);
    pdf.text(notesLines, 20, yPosition);
    yPosition += notesLines.length * 5;
  }

  // Payment Instructions (if from payment gateway)
  if (invoice.paymentConfig && invoice.paymentGateway !== 'manual') {
    yPosition += 10;
    if (yPosition > pageHeight - 60) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...textColor);
    pdf.text('Payment Information:', 20, yPosition);
    
    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(9);
    pdf.text(`Payment Method: ${invoice.paymentGateway.toUpperCase()}`, 20, yPosition);
  }

  // Payment Instructions from branding (if not already shown)
  if (brand.paymentInstructions && !invoice.paymentConfig) {
    yPosition += 10;
    if (yPosition > pageHeight - 60) {
      pdf.addPage();
      yPosition = 20;
    }

    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...textColor);
    pdf.text('Payment Information:', 20, yPosition);

    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...secondaryText);
    const paymentLines = pdf.splitTextToSize(brand.paymentInstructions, pageWidth - 40);
    pdf.text(paymentLines, 20, yPosition);
  }

  // Footer
  const footerY = pageHeight - 20;
  pdf.setFontSize(8);
  pdf.setTextColor(...secondaryText);
  pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, footerY, { align: 'center' });

  pdf.setFontSize(9);
  pdf.setTextColor(...primaryColor);
  const footerText = brand.footerText || 'Thank you for your business!';
  pdf.text(footerText, pageWidth / 2, footerY + 5, { align: 'center' });

  return pdf;
};

/**
 * Download invoice as PDF
 * @param {Object} invoice - Invoice data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 */
export const downloadInvoicePDF = (invoice, userProfile, branding = null) => {
  const pdf = generateInvoicePDF(invoice, userProfile, branding);
  const invoiceNum = invoice.invoiceNumber || 'Invoice';
  const clientName = (invoice.clientName || 'Client').replace(/\s+/g, '_');
  const fileName = `Invoice_${invoiceNum}_${clientName}.pdf`;
  pdf.save(fileName);
};

/**
 * Get PDF as blob for email attachment
 * @param {Object} invoice - Invoice data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 */
export const getInvoicePDFBlob = (invoice, userProfile, branding = null) => {
  const pdf = generateInvoicePDF(invoice, userProfile, branding);
  return pdf.output('blob');
};

/**
 * Open PDF in new window
 * @param {Object} invoice - Invoice data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 */
export const previewInvoicePDF = (invoice, userProfile, branding = null) => {
  const pdf = generateInvoicePDF(invoice, userProfile, branding);
  const pdfBlob = pdf.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl);
};

/**
 * Generate PDF for an estimate
 * @param {Object} estimate - Estimate data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 */
export const generateEstimatePDF = (estimate, userProfile = {}, branding = null) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;

  // Use branding if provided, otherwise defaults
  const brand = branding || DEFAULT_BRANDING;

  const primaryColor = hexToRgb(brand.primaryColor || '#2962ff');
  const textColor = [33, 33, 33];
  const secondaryText = [117, 117, 117];

  let yPosition = 20;

  // Header - use branding company name or user profile
  const companyName = brand.companyName || userProfile.businessName || 'Your Business';
  const tagline = brand.tagline || '';

  pdf.setFontSize(24);
  pdf.setTextColor(...primaryColor);
  pdf.text(companyName, 20, yPosition);

  yPosition += 8;
  pdf.setFontSize(10);
  pdf.setTextColor(...secondaryText);

  if (tagline) {
    pdf.text(tagline, 20, yPosition);
    yPosition += 5;
  }

  if (userProfile.businessAddress) {
    const addressLines = userProfile.businessAddress.split('\n');
    addressLines.forEach(line => {
      pdf.text(line, 20, yPosition);
      yPosition += 5;
    });
  }

  // Estimate Title
  yPosition = 20;
  pdf.setFontSize(28);
  pdf.setTextColor(...primaryColor);
  pdf.text('ESTIMATE', pageWidth - 20, yPosition, { align: 'right' });
  
  yPosition += 10;
  pdf.setFontSize(10);
  pdf.setTextColor(...textColor);
  pdf.text(`Estimate #: ${estimate.estimateNumber}`, pageWidth - 20, yPosition, { align: 'right' });
  
  yPosition += 5;
  pdf.text(`Date: ${formatDate(estimate.issueDate)}`, pageWidth - 20, yPosition, { align: 'right' });
  
  if (estimate.validUntil) {
    yPosition += 5;
    pdf.text(`Valid Until: ${formatDate(estimate.validUntil)}`, pageWidth - 20, yPosition, { align: 'right' });
  }

  yPosition = Math.max(yPosition, 60);
  pdf.setDrawColor(...primaryColor);
  pdf.setLineWidth(0.5);
  pdf.line(20, yPosition, pageWidth - 20, yPosition);
  
  yPosition += 15;

  // Client Info
  pdf.setFontSize(10);
  pdf.setTextColor(...secondaryText);
  pdf.text('PREPARED FOR:', 20, yPosition);
  
  yPosition += 6;
  pdf.setFontSize(12);
  pdf.setTextColor(...textColor);
  pdf.text(estimate.clientName, 20, yPosition);
  
  yPosition += 6;
  pdf.setFontSize(10);
  if (estimate.clientEmail) {
    pdf.text(estimate.clientEmail, 20, yPosition);
    yPosition += 5;
  }

  yPosition += 10;

  // Status Badge
  const statusX = pageWidth - 60;
  const statusY = yPosition - 15;
  
  pdf.setFillColor(229, 231, 235);
  pdf.setTextColor(55, 65, 81);
  pdf.roundedRect(statusX, statusY, 40, 8, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.text(estimate.status.toUpperCase(), statusX + 20, statusY + 5.5, { align: 'center' });

  // Line Items
  pdf.setTextColor(...textColor);
  
  const tableData = estimate.lineItems.map(item => [
    item.description,
    item.quantity ? item.quantity.toString() : '1',
    item.rate ? formatCurrency(item.rate) : '-',
    formatCurrency(item.amount)
  ]);

  autoTable(pdf, {
    startY: yPosition,
    head: [['Description', 'Qty', 'Rate', 'Amount']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: textColor,
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });

  yPosition = (pdf.lastAutoTable?.finalY || yPosition) + 10;

  // Totals
  const totalsX = pageWidth - 85;
  const totalsWidth = 65;

  pdf.setFontSize(10);
  pdf.setTextColor(...textColor);
  pdf.text('Subtotal:', totalsX, yPosition);
  pdf.text(formatCurrency(estimate.subtotal || estimate.totalAmount), totalsX + totalsWidth, yPosition, { align: 'right' });
  
  yPosition += 6;

  if (estimate.tax && estimate.tax > 0) {
    pdf.text('Tax:', totalsX, yPosition);
    pdf.text(formatCurrency(estimate.tax), totalsX + totalsWidth, yPosition, { align: 'right' });
    yPosition += 6;
  }

  if (estimate.discount && estimate.discount > 0) {
    pdf.text('Discount:', totalsX, yPosition);
    pdf.text(`-${formatCurrency(estimate.discount)}`, totalsX + totalsWidth, yPosition, { align: 'right' });
    yPosition += 6;
  }

  pdf.setDrawColor(...primaryColor);
  pdf.line(totalsX, yPosition - 2, pageWidth - 20, yPosition - 2);
  yPosition += 6;
  
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(...primaryColor);
  pdf.text('TOTAL:', totalsX, yPosition);
  pdf.text(formatCurrency(estimate.totalAmount), totalsX + totalsWidth, yPosition, { align: 'right' });
  
  yPosition += 10;

  // Notes
  if (estimate.notes) {
    yPosition += 5;
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...textColor);
    pdf.text('Notes:', 20, yPosition);
    
    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(...secondaryText);
    pdf.setFontSize(9);
    const notesLines = pdf.splitTextToSize(estimate.notes, pageWidth - 40);
    pdf.text(notesLines, 20, yPosition);
    yPosition += notesLines.length * 5;
  }

  // Terms
  if (estimate.terms) {
    yPosition += 10;
    if (yPosition > pageHeight - 80) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...textColor);
    pdf.text('Terms & Conditions:', 20, yPosition);
    
    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...secondaryText);
    const termsLines = pdf.splitTextToSize(estimate.terms, pageWidth - 40);
    pdf.text(termsLines, 20, yPosition);
  }

  // Footer
  const footerY = pageHeight - 20;
  pdf.setFontSize(8);
  pdf.setTextColor(...secondaryText);
  pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, footerY, { align: 'center' });

  pdf.setFontSize(9);
  pdf.setTextColor(...primaryColor);
  pdf.text('Thank you for considering our services!', pageWidth / 2, footerY + 5, { align: 'center' });

  return pdf;
};

/**
 * Download estimate as PDF
 * @param {Object} estimate - Estimate data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 */
export const downloadEstimatePDF = (estimate, userProfile, branding = null) => {
  const pdf = generateEstimatePDF(estimate, userProfile, branding);
  const fileName = `Estimate_${estimate.estimateNumber}_${estimate.clientName.replace(/\s+/g, '_')}.pdf`;
  pdf.save(fileName);
};

/**
 * Preview estimate PDF
 * @param {Object} estimate - Estimate data
 * @param {Object} userProfile - User profile with business info
 * @param {Object} branding - Branding configuration (optional)
 */
export const previewEstimatePDF = (estimate, userProfile, branding = null) => {
  const pdf = generateEstimatePDF(estimate, userProfile, branding);
  const pdfBlob = pdf.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl);
};
